import type { RefObject } from 'react';
import type { Row } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { DENSITY_ROW_HEIGHTS, type TableDensity } from '../types';

// =============================================================================
// Types
// =============================================================================

interface UseTableVirtualizerOptions<TData> {
  containerRef: RefObject<HTMLDivElement | null>;
  rows: Row<TData>[];
  enabled: boolean;
  density: TableDensity;
  overscan?: number;
  estimateSize?: number;
}

export interface VirtualItem<TData> {
  index: number;
  start: number;
  size: number;
  row: Row<TData>;
}

interface UseTableVirtualizerResult<TData> {
  virtualItems: VirtualItem<TData>[];
  totalSize: number;
  isVirtualized: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_OVERSCAN = 5;

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook for virtualizing table rows.
 *
 * When enabled, returns virtual items representing only the visible rows
 * plus an overscan buffer. When disabled, returns empty array and
 * isVirtualized: false, allowing the table to render normally.
 *
 * @param options - Configuration options
 * @returns Virtual items, total size, and virtualization state
 */
export const useTableVirtualizer = <TData>({
  containerRef,
  rows,
  enabled,
  density,
  overscan = DEFAULT_OVERSCAN,
  estimateSize,
}: UseTableVirtualizerOptions<TData>): UseTableVirtualizerResult<TData> => {
  // Calculate row height from density or use override
  const rowHeight = estimateSize ?? DENSITY_ROW_HEIGHTS[density];

  // Always call useVirtualizer (hooks can't be conditional)
  // but we'll ignore results when disabled
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan,
  });

  // Early return if virtualization disabled
  if (!enabled) {
    return { virtualItems: [], totalSize: 0, isVirtualized: false };
  }

  // Map virtual items to include row data
  const virtualItems = virtualizer.getVirtualItems().map((vi) => ({
    index: vi.index,
    start: vi.start,
    size: vi.size,
    row: rows[vi.index],
  }));

  return {
    virtualItems,
    totalSize: virtualizer.getTotalSize(),
    isVirtualized: true,
  };
};
